import { ListingEnums } from '@b-stock/fe-core/locale'
import type {
  LotDto,
  ItemConditionDtoItemConditionsEnum,
} from '@b-stock/listing-api-client'

type PartialLot = Pick<LotDto, 'itemConditions' | 'overrides'>

export const getLotConditions = (
  lot?: PartialLot
): ItemConditionDtoItemConditionsEnum[] => {
  const conditionDtos = lot?.overrides?.itemConditions?.length
    ? lot.overrides.itemConditions
    : lot?.itemConditions
  return (
    conditionDtos?.flatMap(({ itemConditions }) => itemConditions ?? []) ?? []
  )
}

export const useAllConditionsDisplay = (
  lot: PartialLot | null | undefined
): string | undefined => {
  const formatCondition = ListingEnums.ItemCondition.useFormat()

  if (!lot) return undefined

  const conditions = getLotConditions(lot)
  if (!conditions.length) return undefined

  const unique = [...new Set(conditions)]
  return unique.map((c) => formatCondition(c)).join(', ')
}

const MIXED_CONDITION_THRESHOLD = 75

export const useDisplayCondition = (
  lot: PartialLot | null | undefined
): string | undefined => {
  const formatCondition = ListingEnums.ItemCondition.useFormat()

  if (!lot) return undefined

  const conditionDtos = lot.overrides?.itemConditions?.length
    ? lot.overrides.itemConditions
    : lot.itemConditions

  if (!conditionDtos?.length) return undefined

  const dominantDto =
    conditionDtos.length === 1
      ? conditionDtos[0]
      : conditionDtos.reduce<(typeof conditionDtos)[number] | undefined>(
          (best, current) => {
            const pct = current.percentageByQuantity ?? 0
            if (pct <= MIXED_CONDITION_THRESHOLD) return best
            const bestPct = best?.percentageByQuantity ?? 0
            return pct > bestPct ? current : best
          },
          undefined
        )

  if (!dominantDto) return 'Mixed Condition'

  const condition = dominantDto.itemConditions?.[0]
  if (!condition) return undefined

  return formatCondition(condition)
}
